import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User } from "@/types";
import type { AuthTokens } from "@/lib/api/auth";

const API_BASE_URL = import.meta.env.VITE_API_URL || "/api";

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  _hasHydrated: boolean;
  setToken: (token: string) => void;
  setTokens: (token: string, refreshToken?: string) => void;
  setUser: (user: User) => void;
  setAuth: (user: User, tokens: AuthTokens) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  setHasHydrated: (hasHydrated: boolean) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: true,
      _hasHydrated: false,
      setToken: (token) =>
        set({ token, isAuthenticated: true }),
      setTokens: (token, refreshToken) =>
        set({ token, refreshToken: refreshToken ?? null, isAuthenticated: true }),
      setUser: (user) =>
        set({ user, isAuthenticated: true, isLoading: false }),
      setAuth: (user, tokens) =>
        set({
          user,
          token: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          isAuthenticated: true,
          isLoading: false,
        }),
      logout: () => {
        const rt = useAuthStore.getState().refreshToken;
        set({
          token: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
          isLoading: false,
        });
        if (rt) {
          fetch(`${API_BASE_URL}/auth/logout`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ refreshToken: rt }),
          }).catch(() => {});
        }
      },
      setLoading: (loading) =>
        set({ isLoading: loading }),
      setHasHydrated: (hasHydrated) =>
        set({ _hasHydrated: hasHydrated }),
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({ token: state.token, refreshToken: state.refreshToken }),
      onRehydrateStorage: () => (_state, error) => {
        if (error) {
          console.warn("Auth store rehydration failed:", error);
        }
        queueMicrotask(() => {
          useAuthStore.setState({ _hasHydrated: true });
        });
      },
    }
  )
);

if (typeof window !== "undefined") {
  setTimeout(() => {
    if (!useAuthStore.getState()._hasHydrated) {
      console.warn("Auth store hydration timeout - forcing hydration");
      useAuthStore.setState({ _hasHydrated: true });
    }
  }, 1000);
}

// Helper hooks
export function useUser() {
  return useAuthStore((state) => state.user);
}

export function useIsAuthenticated() {
  return useAuthStore((state) => state.isAuthenticated);
}

export function useIsAdmin() {
  const user = useAuthStore((state) => state.user);
  return user?.role === "admin";
}

export function useIsManager() {
  const user = useAuthStore((state) => state.user);
  return user?.role === "admin" || user?.role === "manager";
}

export function useIsViewer() {
  const user = useAuthStore((state) => state.user);
  return user?.role === "viewer";
}

/** Standard user (BE) — reporting access limited to their region */
export function useIsUser() {
  const user = useAuthStore((state) => state.user);
  return user?.role === "user";
}

/** Admin or Manager — can manage users in their scope */
export function useCanManageUsers() {
  const user = useAuthStore((state) => state.user);
  return user?.role === "admin" || user?.role === "manager";
}

/** @deprecated Use useIsManager() instead */
export function useCanManage() {
  return useIsManager();
}
